import { useMemo } from 'react';
import { get, isNull } from 'lodash';
import { ChipProps } from '@mui/material';

import { Nullable } from '~globals/types';
import { CollectItemStateTypes } from '~globals/types/enums';
import { useTypePartnerCompany } from '~hooks/index';

interface OrderPendingStateInfoItem {
  description: string;
  color: NonNullable<ChipProps['color']>;
  tooltipMessage: string;
}

const OrderPendingStateNew = -1;
const OrderPendingStateNotAssigned = -2;

type OrderPendingStateType =
  | typeof OrderPendingStateNew
  | typeof OrderPendingStateNotAssigned
  | CollectItemStateTypes;

type OrderPendingStateList = {
  [state in OrderPendingStateType]: OrderPendingStateInfoItem;
};

interface UseOrderPendingParams {
  carrierCompanyId: Nullable<string>;
  collectItemStateTypeId: Nullable<CollectItemStateTypes>;
}

export const useOrderPendingStateInfo = ({
  carrierCompanyId,
  collectItemStateTypeId,
}: UseOrderPendingParams): OrderPendingStateInfoItem => {
  const { isGiver } = useTypePartnerCompany();

  const stateList = useMemo<OrderPendingStateList>(
    () => ({
      [OrderPendingStateNotAssigned]: {
        description: 'A Asignar',
        color: 'tertiary',
        tooltipMessage: 'A Asignar tooltip',
      },
      [OrderPendingStateNew]: {
        description: 'Recepcionado',
        color: 'quaternary',
        tooltipMessage: 'Ingresada al depósito del transportista.',
      },
      [CollectItemStateTypes.ToValidate]: {
        description: 'Sin Colecta',
        color: 'secondary',
        tooltipMessage: 'El dador la llevara a la base del transportista',
      },
      [CollectItemStateTypes.ToCollect]: {
        description: 'A colectar',
        color: 'secondary',
        tooltipMessage:
          'Lista para que el transportista la retire en el punto de venta.',
      },
      [CollectItemStateTypes.InCollect]: {
        description: 'En colecta',
        color: 'primary',
        tooltipMessage: 'En colecta tooltip.',
      },
      [CollectItemStateTypes.Validated]: {
        description: 'Colectado',
        color: 'success',
        tooltipMessage: 'El transportista ya la tiene en su poder.',
      },
      [CollectItemStateTypes.NotValidated]: {
        description: 'No colectado',
        color: 'error',
        tooltipMessage: 'No colectado tooltip.',
      },
      [CollectItemStateTypes.InWarehouse]: {
        description: 'Recepcionado',
        color: 'quaternary',
        tooltipMessage: 'Ingresada al depósito del transportista.',
      },
    }),
    [],
  );

  const hasCarrier = useMemo(
    () => !isNull(carrierCompanyId),
    [carrierCompanyId],
  );

  const currentInfo = useMemo<OrderPendingStateInfoItem>(() => {
    let type: OrderPendingStateType =
      collectItemStateTypeId ?? CollectItemStateTypes.ToValidate;

    if (!hasCarrier)
      type = isGiver ? OrderPendingStateNotAssigned : OrderPendingStateNew;

    return get(stateList, type);
  }, [collectItemStateTypeId, hasCarrier, stateList, isGiver]);

  return currentInfo;
};
